<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.notifications', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex justify-between items-center">
            <div class="flex space-x-2 items-center">
                <NavLinks :links="meta.navLinks" />
            </div>
            <div class="flex space-x-2 items-center" v-if="meta.links.mark_all_read && meta.unread_notifications_count">
                <span>({{ meta.unread_notifications_count }})</span>
                <InertiaSquareConfirmActionButton
                    :href="meta.links.mark_all_read"
                    :confirm-text="$t('messages.mark_all_notifications_read')"
                    method="delete"
                >
                    <template v-slot:icon>
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                            ></path>
                        </svg>
                    </template>

                    {{ $t('messages.mark_all_notifications_read') }}
                </InertiaSquareConfirmActionButton>
            </div>
        </div>

        <AppWell class="mt-5">
            <div v-if="notifications.length === 0" class="flex flex-col mt-5">
                <EmptyState picture />
            </div>
            <div v-else class="overflow-hidden sm:rounded-md">
                <ul role="list" class="divide-y divide-gray-200">
                    <li class="px-4 py-4 sm:px-6" v-for="notification in notifications" :key="notification.id">
                        <div
                            class="pointer-events-auto md:flex w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5"
                        >
                            <div class="flex-1 p-4">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0 pt-0.5">
                                        <component
                                            v-if="notification.image"
                                            :is="notification.image_url ? 'InertiaLink' : 'span'"
                                            :href="notification.image_url"
                                        >
                                            <img class="h-10 w-10 rounded-full" :src="notification.image" alt="" />
                                        </component>
                                        <div v-else class="h-10 w-10" />
                                    </div>
                                    <div class="ml-3 w-0 flex-1">
                                        <div class="flex justify-between">
                                            <p class="text-sm font-medium text-gray-900">{{ notification.title }}</p>
                                            <div
                                                class="text-xs text-gray-500 pl-2"
                                                :title="notification.created_at_display"
                                            >
                                                {{ notification.time_ago }}
                                            </div>
                                        </div>
                                        <p class="mt-1 text-sm text-gray-500">{{ notification.message }}</p>
                                    </div>
                                </div>
                            </div>
                            <div class="flex justify-end border-l border-gray-200 items-start space-x-1 p-4">
                                <!--                                <InertiaSquareLinkButton-->
                                <!--                                    action="show"-->
                                <!--                                    :href="notification.links.show"-->
                                <!--                                    v-if="notification.links.show"-->
                                <!--                                />-->
                                <a
                                    :href="notification.links.show"
                                    v-if="notification.links.show"
                                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <svg viewBox="0 0 20 20" fill="currentColor" class="eye w-4 h-4">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                        <path
                                            fill-rule="evenodd"
                                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </a>
                                <inertia-link
                                    v-if="notification.read_at === null"
                                    :href="notification.meta.links.mark_as_read"
                                    as="button"
                                    method="delete"
                                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    ><svg
                                        class="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clip-rule="evenodd"
                                        ></path></svg
                                ></inertia-link>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </AppWell>

        <template v-slot:pagination>
            <InertiaPagination :paginator="meta.pagination" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import AppWell from '@/Shared/AppWell.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import NavLinks from '@/components/NavLinks.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';

export default {
    components: {
        PanelPage,
        InertiaPagination,
        AppWell,
        InertiaSquareLinkButton,
        EmptyState,
        NavLinks,
        InertiaSquareConfirmActionButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'notifications'],
};
</script>
